import moment from "moment";

import { OUTER, SECTOR, POSTCODE } from "../consts/layers";
import { FINAL_ROUTES_TIME } from "../consts/timeSlider";
import {
  DEFAULT_PLAN_TYPE,
  DELIVERY_PLAN_TYPES,
  NON_DELIVERY_PLAN_TYPES,
} from "../consts/planTypes";

import { Postcode } from "./postcode";
import { timeBandsSorter } from "./routeMatrix";
import { isNonRedirectionRouteCode, isRedirectionRouteCode } from "./routes";

export const processRoutesTimes = ({
  routeConfig = [],
  routes,
  groupedPostcodes,
  groupedSectors,
  planType = DEFAULT_PLAN_TYPE,
  change = null,
  routesTimes = null,
  routesByOuter = null,
  cutOffTimes = null,
}) => {
  if (NON_DELIVERY_PLAN_TYPES.includes(planType))
    return {
      routesTimes: routesTimes || {},
      routesByOuter:
        routesByOuter ||
        DELIVERY_PLAN_TYPES.reduce((acc, planType) => {
          acc[planType] = {};
          return acc;
        }, {}),
      cutOffTimes: cutOffTimes || [],
    };

  const routesByCode = routes.reduce((acc, route) => {
    acc[route.routeCode] = route;
    return acc;
  }, {});

  const prevTimeBands = {};
  const areaTimes = {};
  routesTimes = { ...(routesTimes || {}) };
  cutOffTimes = { ...(cutOffTimes || {}) };
  routesByOuter = DELIVERY_PLAN_TYPES.reduce((acc, planType) => {
    acc[planType] = { ...(routesByOuter?.[planType] || {}) };
    return acc;
  }, {});
  if (change)
    ({ routesTimes, routesByOuter, cutOffTimes } = cloneShallowWithoutOuter({
      change,
      planType,
      routesTimes,
      routesByOuter,
      cutOffTimes,
    }));

  routeConfig = [...routeConfig].sort(timeBandsSorter);
  routeConfig.forEach(({ plannedReadyTimeBand }) => {
    prevTimeBands[plannedReadyTimeBand] = Object.keys(areaTimes).reverse();
    areaTimes[plannedReadyTimeBand] = DELIVERY_PLAN_TYPES.reduce(
      (acc, planType) => {
        acc[planType] = { outer: {}, sector: {}, postcode: {} };
        return acc;
      },
      {}
    );
  });

  const getStartTimes = ({ timeBand, levelKey, postcodeKey, routeType }) => {
    const areaPrevTimeBand = prevTimeBands[timeBand].find(
      (prevTimeBand) =>
        areaTimes[prevTimeBand][routeType][levelKey][postcodeKey]
    );
    if (!areaPrevTimeBand)
      return {
        startTime: "23:00",
        redirectStartTime: "23:00",
      };
    const { routeCode, endTime: prevEndTime } =
      areaTimes[areaPrevTimeBand][routeType][levelKey][postcodeKey];

    const startTime = moment(prevEndTime, "HH:mm").add(15, "m").format("HH:mm");
    let redirectStartTime = startTime;
    if (isRedirectionRouteCode(routeCode)) {
      const areaPrevNonRedirectionTimeBand = prevTimeBands[
        areaPrevTimeBand
      ].find(
        (prevTimeBand) =>
          areaTimes[prevTimeBand][routeType][levelKey][postcodeKey] &&
          isNonRedirectionRouteCode(
            areaTimes[prevTimeBand][routeType][levelKey][postcodeKey].routeCode
          )
      );
      if (areaPrevNonRedirectionTimeBand) {
        const { endTime: prevNonRedirectionEndTime } =
          areaTimes[areaPrevNonRedirectionTimeBand][routeType][levelKey][
            postcodeKey
          ];
        redirectStartTime = moment(prevNonRedirectionEndTime, "HH:mm")
          .add(15, "m")
          .format("HH:mm");
      } else {
        redirectStartTime = "23:00";
      }
    }

    return {
      startTime,
      redirectStartTime,
    };
  };

  const processArea = ({ timeBand, config, levelKey }) => {
    const route = routesByCode[config.routeCode];
    if (!route) return;
    const { routeCode, routeType, plannedReadyTimeAsString } = route;

    const outer = Postcode.toOuter(config[levelKey]);
    if (!routesByOuter[routeType][outer])
      routesByOuter[routeType][outer] = new Set();
    routesByOuter[routeType][outer].add(routeCode);

    const postcodeKey = config[levelKey];
    const { startTime, redirectStartTime } = getStartTimes({
      timeBand,
      levelKey,
      postcodeKey,
      routeType,
    });
    const endTime = plannedReadyTimeAsString || FINAL_ROUTES_TIME;

    if (areaTimes[timeBand][routeType][levelKey][postcodeKey]) {
      const { routeCode: inheritedRouteCode } =
        areaTimes[timeBand][routeType][levelKey][postcodeKey];
      inheritedRouteCode &&
        delete routesTimes[inheritedRouteCode][levelKey][postcodeKey];
    }
    if (!routesTimes[routeCode])
      routesTimes[routeCode] = {
        outer: {},
        sector: {},
        postcode: {},
      };
    routesTimes[routeCode][levelKey][postcodeKey] = {
      startTime,
      redirectStartTime,
    };

    areaTimes[timeBand][routeType][levelKey][postcodeKey] = {
      startTime,
      redirectStartTime,
      endTime,
      routeCode,
    };

    if (levelKey === "outer") {
      const sectors = groupedSectors[config[levelKey]] || [];
      sectors.forEach((sector) => {
        processArea({
          timeBand,
          config: { ...config, sector },
          levelKey: "sector",
        });
      });
    } else if (levelKey === "sector") {
      const postcodes = groupedPostcodes[config[levelKey]] || [];
      postcodes.forEach((postcode) => {
        processArea({
          timeBand,
          config: { ...config, postcode },
          levelKey: "postcode",
        });
      });
    }
  };

  routeConfig.forEach((config) => {
    config.outer
      .filter(
        ({ outer, routeType }) =>
          !change || (change.outer === outer && planType === routeType)
      )
      .forEach((outer) => {
        processArea({
          timeBand: config.plannedReadyTimeBand,
          config: outer,
          levelKey: "outer",
        });
        outer.sector?.forEach((sector) =>
          processArea({
            timeBand: config.plannedReadyTimeBand,
            config: sector,
            levelKey: "sector",
          })
        );
        outer.postcode?.forEach((postcode) =>
          processArea({
            timeBand: config.plannedReadyTimeBand,
            config: postcode,
            levelKey: "postcode",
          })
        );
      });
  });

  return {
    routesTimes,
    routesByOuter,
    cutOffTimes: {
      ...cutOffTimes,
      ...getCutOffTimes({ areaTimes, planType }),
    },
  };
};

const cloneShallowWithoutOuter = ({
  change,
  planType,
  routesTimes,
  routesByOuter,
  cutOffTimes,
}) => {
  return {
    routesTimes: cloneRoutesTimesWithoutOuter({
      change,
      routesTimes,
      planType,
    }),
    routesByOuter: cloneRoutesByOuterWithoutOuter({
      change,
      planType,
      routesByOuter,
    }),
    cutOffTimes: cloneCutOffTimesWithoutOuter({
      change,
      cutOffTimes,
    }),
  };
};

const cloneRoutesTimesWithoutOuter = ({ change, routesTimes }) => {
  const clone = {};
  Object.keys(routesTimes).forEach((routeCode) => {
    if (!change.impactRouteCodes.includes(routeCode))
      return (clone[routeCode] = routesTimes[routeCode]);

    clone[routeCode] = {};
    [OUTER, SECTOR, POSTCODE].forEach((level) => {
      const levelKey = level.toLowerCase();
      clone[routeCode][levelKey] = {};
      Object.keys(routesTimes[routeCode]?.[levelKey] || {}).forEach(
        (postcodeKey) => {
          if (Postcode.toOuter(postcodeKey) !== change.outer)
            clone[routeCode][levelKey][postcodeKey] =
              routesTimes[routeCode][levelKey][postcodeKey];
        }
      );
    });
  });
  return clone;
};

const cloneRoutesByOuterWithoutOuter = ({
  change,
  planType,
  routesByOuter,
}) => {
  const clone = {};
  DELIVERY_PLAN_TYPES.forEach((type) => {
    if (planType !== type) return (clone[type] = routesByOuter[type]);
    clone[type] = Object.keys(routesByOuter[type]).reduce((acc, outerKey) => {
      if (outerKey !== change.outer)
        acc[outerKey] = routesByOuter[type][outerKey];
      return acc;
    }, {});
  });
  return clone;
};

const cloneCutOffTimesWithoutOuter = ({ change, cutOffTimes }) => {
  const clone = { ...cutOffTimes };
  change.sectors.forEach((sector) => {
    change.postcodes[sector].forEach((postcode) => {
      delete clone[postcode];
    });
  });
  return clone;
};

const getCutOffTimes = ({ areaTimes, planType }) => {
  const cutOffTimes = {};
  Object.keys(areaTimes).forEach((timeband) =>
    Object.keys(areaTimes[timeband][planType].postcode).forEach((postcode) => {
      const { routeCode, endTime } =
        areaTimes[timeband][planType].postcode[postcode];
      if (isRedirectionRouteCode(routeCode)) return;
      else if (!cutOffTimes[postcode]) cutOffTimes[postcode] = [endTime];
      else cutOffTimes[postcode].push(endTime);
    })
  );
  return cutOffTimes;
};
